/**
 * Resolves the submission bot's `rpa_name` for agent-steps reconstruction.
 *
 * Prefers `workflow_config.submission_type` → known map. When that is missing
 * or unmapped, falls back to the order's audit-trail overview and picks the
 * best matching RPA entry so empty `submission_type` does not block the fetch.
 *
 * @module resolveSubmissionRpaForAgentSteps
 */
import { getAuditTrialOverview } from "../../../api/postCall/getAuditTrialOverview";
import { NewEventModel } from "../../../data-model/eventsModal";
import {
  resolveSubmissionRpaName,
  SUBMISSION_TYPE_TO_RPA_NAME,
} from "./submissionAgentStepsConfig";

/** Tag used by the audit-trail detail endpoint for RPA workflow entries. */
export const RPA_NAME_TAG = "rpa_name";

/** Known submission bot `rpa_name` values from the submission_type map. */
const KNOWN_SUBMISSION_RPA_NAMES: ReadonlySet<string> = new Set(
  Object.values(SUBMISSION_TYPE_TO_RPA_NAME),
);

/**
 * Heuristic for RPA names that look like a PA submission bot when the name is
 * not in the known map (e.g. a newly onboarded portal). Deliberately narrow so
 * adjacent bots like `auth_on_file_check` / NAR are never treated as submission.
 *
 * @input name - Candidate `rpa_name` from the overview.
 * @returns True when the name likely belongs to a PA submission bot.
 */
const looksLikePaSubmissionRpa = (name: string): boolean =>
  /pa_submission/i.test(name);

/**
 * Picks the best submission `rpa_name` from an audit-trail overview.
 *
 * Only considers known submission bots or names containing `pa_submission`.
 * Other RPA entries (e.g. `auth_on_file_check`) are ignored — never used as a
 * catch-all — so an empty/unmapped `submission_type` cannot latch onto the
 * wrong bot. Within the matching pool, the latest timestamp wins.
 *
 * @input entries - Overview entries for the order (api / rpa / screen).
 * @returns The chosen `rpa_name`, or undefined when no submission RPA exists.
 */
export const pickSubmissionRpaNameFromOverview = (
  entries: NewEventModel[] | undefined,
): string | undefined => {
  const rpaEntries = (entries ?? []).filter(
    (entry): entry is NewEventModel & { name: string } =>
      entry.tag === RPA_NAME_TAG &&
      typeof entry.name === "string" &&
      entry.name.length > 0,
  );

  if (rpaEntries.length === 0) {
    return undefined;
  }

  const knownMatches = rpaEntries.filter((entry) =>
    KNOWN_SUBMISSION_RPA_NAMES.has(entry.name),
  );
  const heuristicMatches = rpaEntries.filter((entry) =>
    looksLikePaSubmissionRpa(entry.name),
  );
  const pool = knownMatches.length > 0 ? knownMatches : heuristicMatches;

  if (pool.length === 0) {
    return undefined;
  }

  const sorted = [...pool].sort((a, b) => {
    const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return aTime - bTime;
  });

  return sorted[sorted.length - 1]?.name;
};

/**
 * Resolves the submission bot `rpa_name` for an order.
 *
 * Tries `submission_type` first; if that yields nothing, queries the audit-trail
 * overview for the order and picks a matching RPA entry.
 *
 * @input orderId - Order whose audit trail should be inspected on fallback.
 * @input submissionType - `workflow_config.submission_type`, if any.
 * @returns The resolved `rpa_name`, or undefined when none can be discovered.
 */
export const resolveSubmissionRpaForAgentSteps = async (
  orderId: string,
  submissionType: string | undefined,
): Promise<string | undefined> => {
  const fromConfig = resolveSubmissionRpaName(submissionType);
  if (fromConfig) {
    return fromConfig;
  }

  const overview = await getAuditTrialOverview({ order_id: orderId });
  return pickSubmissionRpaNameFromOverview(overview.entries);
};
